/**
 * The window which shuts an account out of the server.
 *
 * One dialog for the whole screen: the button pressed says which account it
 * is about, and its address and title are written into the form before the
 * dialog opens. Rendering one per row would repeat the same four fields once
 * for every account on the page.
 *
 * The button is a link, and the link is where a browser with no dialogs of
 * its own lands: the banlist form, already pointed at the same nick. Only the
 * click is taken over, and only when there is a dialog to take it over with.
 */

import { dialogsWork } from "./modal";
import { getJson } from "../core/http";
import { query, queryAll } from "../core/dom";
import { text } from "../core/i18n";

/** One address an account has been seen signing in from. */
interface AddressResponse {
    readonly ip_address: string;
    readonly last_seen: string;
    readonly times: number;
}

/** What the endpoint of the addresses answers. */
interface AddressListResponse {
    readonly addresses: readonly AddressResponse[];
}

/**
 * Keep the date beside the type of a punishment honest.
 *
 * A permanent ban has nothing to be valid until, so the field is greyed out
 * and emptied rather than left there to be filled in and thrown away by the
 * endpoint. A temporary one gets it back.
 *
 * A mute is the other way round: it always ends, so the field is not merely
 * available but required. Saying so on the field means somebody is told
 * while they are filling it in rather than after they have pressed the
 * button - and the endpoint refuses one without an end either way.
 */
export function bindPunishmentType(scope: ParentNode): void {
    const type = query<HTMLSelectElement>("[data-ban-type]", scope);
    const until = query<HTMLInputElement>("[data-ban-until]", scope);

    if (!type || !until) {
        return;
    }

    // The dialog is wired again every time it opens, on the same fields.
    const known = type.dataset["banBound"] === "yes";
    type.dataset["banBound"] = "yes";

    const follow = (): void => {
        const chosen = type.value.toUpperCase();
        const permanent = chosen === "PERMANENT";
        const mute = chosen === "MUTE";

        until.disabled = permanent;
        until.required = mute;
        until.closest(".field")?.classList.toggle("is-inactive", permanent);

        if (permanent) {
            until.value = "";
        }

        // A mute reaches none of the game, the portal or the addresses: it
        // takes the writing and leaves the account alone. The boxes are
        // switched off rather than left to be ticked and ignored.
        for (const box of queryAll<HTMLInputElement>("[data-ban-reach]", scope)) {
            if (mute) {
                box.checked = false;
                box.dispatchEvent(new Event("change", { bubbles: true }));
            }

            box.disabled = mute;
            box.closest(".checkbox")?.classList.toggle("is-inactive", mute);
        }

        const game = query<HTMLInputElement>("[data-ban-game]", scope);
        if (game) {
            game.checked = !mute;
            game.closest(".checkbox")?.classList.toggle("is-inactive", mute);
        }

        const hint = query<HTMLElement>("[data-ban-mute-hint]", scope);
        if (hint) {
            hint.hidden = !mute;
        }
    };

    if (!known) {
        type.addEventListener("change", follow);
    }

    follow();
}

/**
 * Keep the button which bans out of reach until there is a reason to press it.
 *
 * The endpoint refuses a punishment without one anyway; this is the same
 * refusal, said before somebody has typed a date and picked a type.
 */
export function bindReasonGuard(scope: ParentNode): void {
    const reason = query<HTMLTextAreaElement>("[data-ban-reason]", scope);
    const submit = query<HTMLButtonElement>("[data-ban-submit]", scope);

    if (!reason || !submit) {
        return;
    }

    const known = reason.dataset["banBound"] === "yes";
    reason.dataset["banBound"] = "yes";

    const follow = (): void => {
        submit.disabled = reason.value.trim().length < 3;
    };

    if (!known) {
        reason.addEventListener("input", follow);
    }

    follow();
}

/** Return the title of the dialog, naming the account it is about. */
export function banTitle(nick: string): string {
    return text("admin.users.ban_title", "Zabanovať účet {username}", { username: nick });
}

/** Point the dialog at one account and open it. */
export function openBanDialog(dialog: HTMLDialogElement, opener: HTMLElement): void {
    const form = query<HTMLFormElement>("[data-ban-form]", dialog);
    const title = query<HTMLElement>("[data-ban-title]", dialog);
    const action = opener.dataset["banAction"] ?? "";

    if (!form || !action) {
        return;
    }

    form.action = action;

    if (title) {
        title.textContent = banTitle(opener.dataset["banNick"] ?? "");
    }

    form.reset();
    bindPunishmentType(form);
    bindReasonGuard(form);
    dialog.showModal();
    query<HTMLTextAreaElement>("[data-ban-reason]", form)?.focus();
}

/** Wire every ban button of the current page. */
export function initBanDialog(): void {
    const dialog = query<HTMLDialogElement>("[data-ban-dialog]");
    const openers = queryAll<HTMLElement>("[data-ban-user]");

    if (!dialog || openers.length === 0 || !dialogsWork()) {
        return;
    }

    for (const opener of openers) {
        opener.addEventListener("click", (event: MouseEvent) => {
            if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey) {
                return;
            }

            event.preventDefault();
            openBanDialog(dialog, opener);
        });
    }

    query<HTMLElement>("[data-ban-cancel]", dialog)?.addEventListener("click", () => dialog.close());
}

/** Return the sprite icon `name`, as the templates draw it. */
function iconMarkup(name: string, size: number): string {
    return (
        `<svg class="icon" width="${size}" height="${size}" aria-hidden="true">` +
        `<use href="/static/assets/icons/icons.svg#${name}"></use></svg>`
    );
}

/**
 * Say into `panel` that there is nothing to pick from.
 *
 * The same framed line the server draws in the same place, rather than a bare
 * sentence dropped into a panel - the two are read one after the other while
 * an account is being picked, and they should not look like two portals.
 */
function quietNote(panel: HTMLElement, message: string, name: string): void {
    const note = document.createElement("div");
    note.className = "notice notice--muted";
    note.innerHTML = iconMarkup(name, 16);

    const words = document.createElement("span");
    words.textContent = message;
    note.append(words);

    panel.replaceChildren(note);
}

/** Return one row of the address table, for the given sighting. */
function addressRow(address: AddressResponse, ticked: boolean): HTMLTableRowElement {
    const row = document.createElement("tr");

    const where = document.createElement("td");
    const code = document.createElement("code");
    code.className = "log-address";
    code.textContent = address.ip_address;
    where.append(code);

    const when = document.createElement("td");
    when.textContent = new Date(address.last_seen).toLocaleString();

    const times = document.createElement("span");
    times.className = "cell-sub";
    times.textContent = text("js.bans.times", "Prihlásení: {count}", { count: address.times });
    when.append(times);

    const choice = document.createElement("td");
    choice.className = "actions";

    const label = document.createElement("label");
    label.className = "checkbox";

    const box = document.createElement("input");
    box.type = "checkbox";
    box.name = "addresses";
    box.value = address.ip_address;
    box.checked = ticked;

    const caption = document.createElement("span");
    caption.textContent = text("admin.bans.block_address", "Zablokovať");

    label.append(box, caption);
    choice.append(label);
    row.append(where, when, choice);

    return row;
}

/** Return one heading of the table, as the `column` macro writes it. */
function columnHead(label: string, name: string): HTMLTableCellElement {
    const cell = document.createElement("th");
    const head = document.createElement("span");
    head.className = "column-head";
    head.innerHTML = iconMarkup(name, 12);
    head.append(` ${label}`);
    cell.append(head);

    return cell;
}

/** Return the table the addresses of one account are drawn in. */
function addressTable(addresses: readonly AddressResponse[], ticked: ReadonlySet<string>): HTMLElement {
    const frame = document.createElement("div");
    frame.className = "table-wrap table-wrap--framed";

    const table = document.createElement("table");
    table.className = "data";

    const head = document.createElement("thead");
    const heading = document.createElement("tr");
    heading.append(
        columnHead(text("admin.bans.column_address", "IP adresa"), "globe"),
        columnHead(text("admin.users.access_last", "Naposledy"), "clock"),
        document.createElement("th"),
    );
    head.append(heading);
    table.append(head);

    const body = document.createElement("tbody");
    for (const address of addresses) {
        body.append(addressRow(address, ticked.has(address.ip_address)));
    }

    table.append(body);
    frame.append(table);

    return frame;
}

/** Draw the addresses of *nick* into the panel of the form. */
async function fillAddresses(panel: HTMLElement, nick: string): Promise<void> {
    const ticked = new Set(
        queryAll<HTMLInputElement>("input[name=addresses]", panel)
            .filter((box) => box.checked)
            .map((box) => box.value),
    );

    if (!nick) {
        quietNote(panel, text("js.bans.no_account", "Najprv vyberte účet."), "user");

        return;
    }

    try {
        const answer = await getJson<AddressListResponse>(`/api/admin/bans/addresses/${encodeURIComponent(nick)}`);

        if (answer.addresses.length === 0) {
            quietNote(panel, text("js.bans.no_addresses", "Z tohto účtu sa zatiaľ nikto neprihlásil."), "globe");

            return;
        }

        panel.replaceChildren(addressTable(answer.addresses, ticked));
    } catch {
        quietNote(panel, text("js.bans.addresses_failed", "IP adresy sa nepodarilo načítať."), "alert-triangle");
    }
}

/**
 * Wire the form of one punishment.
 *
 * The addresses belong to the account which is being punished, and that
 * account is picked while the form is open - so the list is fetched when the
 * nick changes rather than drawn once with the page.
 */
export function initBanEditor(): void {
    const form = query<HTMLFormElement>("[data-ban-editor]");
    if (!form) {
        return;
    }

    bindPunishmentType(form);

    const nick = query<HTMLSelectElement | HTMLInputElement>("[data-ban-nick]", form);
    const panel = query<HTMLElement>("[data-ban-addresses]", form);
    const toggle = query<HTMLInputElement>("[data-ban-addresses-toggle]", form);

    if (!nick || !panel || !toggle) {
        return;
    }

    // Which account the panel is currently showing. The page arrives with the
    // addresses of the nick it was opened on already drawn, so that is where
    // this starts - and why ticking the box asks the portal for nothing.
    let drawn = nick.value.trim();

    const refresh = (): void => {
        const wanted = nick.value.trim();
        if (!toggle.checked || wanted === drawn) {
            return;
        }

        drawn = wanted;
        void fillAddresses(panel, wanted);
    };

    toggle.addEventListener("change", () => {
        if (toggle.checked && panel.querySelector("input[name=addresses]") === null) {
            drawn = nick.value.trim();
            void fillAddresses(panel, drawn);
        }
    });
    nick.addEventListener("change", refresh);
}
